import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyledForms } from '@app/components/styled/form/Form';
import { ShopSelect } from '@app/components/form/selects/features/ShopSelect';
import { PaymentMethodSelect } from '@app/components/form/selects/features/PaymentMethodSelect';
import { PaymentProviderTypeSelect } from '@app/components/form/selects/features/PaymentProviderTypeSelect';
import { TextInput } from '@app/components/form/TextInput';
import { BaseSelect } from '@app/components/form/selects/BaseSelect';
import { createColumnHelper } from '@tanstack/react-table';
import { PriceInput } from '@app/components/form/PriceInput';
import { IShop, PaymentProvider } from '@app/common';
import { FormSection } from '@app/components/styled/form/FormSection';
import { PaymentProviderFormValues } from './payment-provider-form.helpers';

const columnHelper = createColumnHelper<IShop>();

const paygreenSelectedColumns = [
  columnHelper.accessor('publicName', {
    header: () => (
      <BaseSelect.ListHeaderItem width="20%">
        Nom
      </BaseSelect.ListHeaderItem >
    ),
    cell: (info) => (
      <BaseSelect.ListItemCol width="20%">
        <BaseSelect.ListItemText numberOfLines={1} bold>
          {info.getValue() ?? 'Restaurant supprimé'}
        </BaseSelect.ListItemText>
      </BaseSelect.ListItemCol>
    ),
  }),
  columnHelper.display({
    id: 'shopId',
    header: () => (
      <BaseSelect.ListHeaderItem width="60%">
        Identifiant boutique
      </BaseSelect.ListHeaderItem>
    ),
    cell: (info) => (
      <BaseSelect.ListItemCol width="60%">
        <BaseSelect.ListItemText numberOfLines={1} bold>
          <TextInput name={`shops.${info.row.index}.shopId`} size='small' />
        </BaseSelect.ListItemText>
      </BaseSelect.ListItemCol>
    ),
  }),
  columnHelper.display({
    id: 'fees',
    header: () => (
      <BaseSelect.ListHeaderItem width="20%">
        Frais (€)
      </BaseSelect.ListHeaderItem>
    ),
    cell: (info) => (
      <BaseSelect.ListItemCol width="20%">
        <BaseSelect.ListItemText numberOfLines={1} bold>
          <PriceInput name={`shops.${info.row.index}.fees`} size='small' />
        </BaseSelect.ListItemText>
      </BaseSelect.ListItemCol>
    ),
  }),
];

const worldlineSelectedColumns = [
  columnHelper.accessor('publicName', {
    header: () => (
      <BaseSelect.ListHeaderItem width="60%">
        Nom
      </BaseSelect.ListHeaderItem >
    ),
    cell: (info) => (
      <BaseSelect.ListItemCol width="60%">
        <BaseSelect.ListItemText numberOfLines={1} bold>
          {info.getValue() ?? 'Restaurant supprimé'}
        </BaseSelect.ListItemText>
      </BaseSelect.ListItemCol>
    ),
  }),
  columnHelper.display({
    id: 'fees',
    header: () => (
      <BaseSelect.ListHeaderItem width="40%">
        Frais (€)
      </BaseSelect.ListHeaderItem>
    ),
    cell: (info) => (
      <BaseSelect.ListItemCol width="40%">
        <BaseSelect.ListItemText numberOfLines={1} bold>
          <PriceInput name={`shops.${info.row.index}.fees`} size='small' />
        </BaseSelect.ListItemText>
      </BaseSelect.ListItemCol>
    ),
  }),
];

interface PaymentProviderFormProps {
  isEditing?: boolean;
}

export const PaymentProviderForm = ({ isEditing = false }: PaymentProviderFormProps) => {
  const { control } = useFormContext<PaymentProviderFormValues>();
  const providerType = useWatch({ control, name: 'type' });

  return (
    <StyledForms.Form>

      <FormSection.Section>
        <FormSection.Title>Type de prestataire</FormSection.Title>
        <FormSection.Content>

          <PaymentProviderTypeSelect
            name="type"
            label="Prestataire de paiement"
            disabled={isEditing}
          />

        </FormSection.Content>
      </FormSection.Section>

      {providerType === PaymentProvider.PAYGREEN && (
        <FormSection.Section>
          <FormSection.Title>Configuration Paygreen</FormSection.Title>
          <FormSection.Content>

            <ShopSelect
              name="shops"
              selectedColumns={paygreenSelectedColumns}
              isMultiple />

            <PaymentMethodSelect name="paymentMethods" isMultiple />

            <TextInput name="rootShopId" label="Identifiant de la boutique" />

            <TextInput name="publicKey" label="Clé publique" />

            <TextInput name="secretKey" label="Clé secrète" />

          </FormSection.Content>
        </FormSection.Section>
      )}

      {providerType === PaymentProvider.WORLDLINE && (
        <FormSection.Section>
          <FormSection.Title>Configuration Worldline</FormSection.Title>
          <FormSection.Content>

            <ShopSelect
              name="shops"
              selectedColumns={worldlineSelectedColumns}
              isMultiple />

            <PaymentMethodSelect name="paymentMethods" isMultiple />

            <TextInput name="merchantId" label="Identifiant marchand (Merchant ID)" />

            <TextInput name="apiKey" label="Clé API" />

            <TextInput name="apiSecret" label="Secret API" />

          </FormSection.Content>
        </FormSection.Section>
      )}

    </StyledForms.Form>
  );
};
